import { createSelector } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux/rootReducer"
import { memoize } from "nodash"
import {
    ImageColorFinderFlight,
    InfraredFlight,
    LiveVideoFlight,
    OrthoFlight,
    PanoramaFlight,
    SatelliteFlight,
    UserLocationShare,
    VideoColorFinderFlight,
    VideoFlight,
} from "@nova/protobuf"
import { WeakObj } from "@nova/weak-obj"
import {
    HotspotPointTypes,
    ID,
    InfraredFlightData,
    OrthoPalettes,
    SidePanelLayerOrGroup,
} from "@nova/constants"
import { dateFromNumber, decodeNumberToRGBA } from "@nova/serializer/src/client/util"
import { FlightId, ShowLayerByIdMap } from "features/map/layers/types"
import { ScanAreaData } from "./types"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { selectStarredAtlasLiveVideoFlights } from "./slice"

const root = (state: RootState) => state.project.data

export const createInfraredFlightData = () => {
    const flight = InfraredFlight.create() as Omit<InfraredFlight, "index"> & { index?: string }
    delete flight["index"]
    delete flight["parentId"]
    delete flight["mergeId"]
    return flight as InfraredFlightData
}

export const selectInfraredFlights = (state: RootState) =>
    root(state).infraredFlights as InfraredFlight[]

export const selectNonmergedInfraredFlights = createSelector([selectInfraredFlights], (flights) => {
    return flights.filter((flight) => !flight.mergeId)
})

export const selectNonArchivedInfraredFlights = createSelector([selectInfraredFlights], (flights) =>
    flights.filter((flight) => !flight.archivedTimestamp)
)
export const selectVisibleInfraredFlights = createSelector(
    [selectNonmergedInfraredFlights, selectAllEntitiesMap],
    (flights, entityMap) =>
        flights.filter(
            (e) => e.visible && !getAncestors(e.id, entityMap).find((ancestor) => !ancestor.visible)
        )
)

export const selectAllInfraredFlightsMap = createSelector([selectInfraredFlights], (flights) =>
    flights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, InfraredFlight>
    )
)
export const selectAnyInfraredFlightUsesHexPoints = createSelector(
    [selectInfraredFlights],
    (flights) => flights.some((flight) => flight.pointType === HotspotPointTypes.Hex)
)
export const selectNonMergedInfraredFlightsMap = createSelector(
    [selectNonmergedInfraredFlights],
    (flights) =>
        flights.reduce(
            (acc, cur) => {
                acc[cur.id] = cur
                return acc
            },
            {} as Record<string, InfraredFlight>
        )
)
export const selectInfraredFlightById = memoize((id: string) =>
    createSelector([selectAllInfraredFlightsMap], (map) => map[id])
)
export const selectInfraredScanAreaData = memoize(() =>
    createSelector(
        [selectFlightsWithHotspotScanArea, selectAllInfraredFlightsMap],
        (scanAreaLayerById, flights) => {
            const activeFlightIds = Object.keys(scanAreaLayerById).filter(
                (key) => scanAreaLayerById[key]
            )
            return activeFlightIds.reduce((scanAreas, flightId) => {
                const flight = flights[flightId]
                if (!flight.job?.scanArea || flight.job.scanArea.length === 0) {
                    return scanAreas
                }
                scanAreas.push({
                    coordinates: flight.job?.scanArea || [],
                    fillColor: decodeNumberToRGBA(flight.scanAreaFillColor || 0),
                    outlineColor: decodeNumberToRGBA(flight.scanAreaOutlineColor || 0),
                    outlineThickness: 2.5,
                })
                return scanAreas
            }, [] as ScanAreaData[])
        }
    )
)

export const selectInfraredFlightDataById = memoize((id: string) => {
    const weakObj = new WeakObj(InfraredFlight.create(), [
        "filters",
        "hotspots",
        "id",
        "images",
        "integrations",
        "job",
        "name",
        "orthoBoundingBox",
        "orthoGradient",
        "perimeterDistance",
        "pointColor",
        "pointType",
        "polyFill",
        "createdAt",
        "createdBy",
        "polyStroke",
        "relativeSizeEnabled",
        "sensitivity",
        "visible",
    ])
    return createSelector([selectAllInfraredFlightsMap], (map) =>
        map[id] ? weakObj.refFrom(map[id]) : undefined
    )
})

export const selectInfraredFlightPerimeterRange = memoize((id: string) => {
    const weakObj = new WeakObj(createInfraredFlightData(), ["perimeterDistance"])
    return createSelector([selectInfraredFlightDataById(id)], (flight) =>
        flight ? weakObj.refFrom(flight) : undefined
    )
})
export const selectRawInfraredImagesByFlightId = memoize((id: string) =>
    createSelector([selectInfraredFlightById(id)], (flight) => flight?.images || [])
)

export const selectSatelliteFlights = (state: RootState) => root(state).satelliteFlights
export const selectSatelliteFlightsMap = createSelector([selectSatelliteFlights], (flights) =>
    flights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, SatelliteFlight>
    )
)
export const selectSatelliteFlightById = memoize((id: string) =>
    createSelector([selectSatelliteFlightsMap], (map) => map[id])
)
export const selectNonArchivedSatelliteFlights = createSelector(
    [selectSatelliteFlights],
    (flights) => flights.filter((flight) => !flight.archivedTimestamp)
)
export const selectVisibleSatelliteFlights = createSelector(
    [selectNonArchivedSatelliteFlights, selectAllEntitiesMap],
    (flights, entityMap) =>
        flights.filter(
            (e) => e.visible && !getAncestors(e.id, entityMap).find((ancestor) => !ancestor.visible)
        )
)

export const selectOrthoFlights = (state: RootState) => root(state).orthoFlights
export const selectNonArchivedOrthoFlights = createSelector([selectOrthoFlights], (flights) =>
    flights.filter((flight) => !flight.archivedTimestamp)
)
export const selectVisibleOrthoFlights = createSelector(
    [selectNonArchivedOrthoFlights, selectAllEntitiesMap],
    (flights, entityMap) =>
        flights.filter(
            (e) => e.visible && !getAncestors(e.id, entityMap).find((ancestor) => !ancestor.visible)
        )
)

export const selectOrthoFlightsMap = createSelector([selectOrthoFlights], (flights) =>
    flights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, OrthoFlight>
    )
)
export const selectOrthoFlightById = memoize((id: string) =>
    createSelector([selectOrthoFlightsMap], (map) => map[id])
)
export const selectOrthoScanAreaData = memoize(() =>
    createSelector(
        [selectFlightsWithOrthoScanArea, selectOrthoFlightsMap],
        (scanAreaLayerById, flights) => {
            const activeFlightIds = Object.keys(scanAreaLayerById).filter(
                (key) => scanAreaLayerById[key]
            )
            return activeFlightIds.reduce((scanAreas, flightId) => {
                const flight = flights[flightId]
                if (!flight.job?.scanArea || flight.job.scanArea.length === 0) {
                    return scanAreas
                }
                scanAreas.push({
                    coordinates: flight.job?.scanArea || [],
                    fillColor: decodeNumberToRGBA(flight.scanAreaFillColor || 0),
                    outlineColor: decodeNumberToRGBA(flight.scanAreaOutlineColor || 0),
                    outlineThickness: 2.5,
                })
                return scanAreas
            }, [] as ScanAreaData[])
        }
    )
)
export const selectRawOrthoImagesByFlightId = memoize((id: string) =>
    createSelector([selectOrthoFlightById(id)], (flight) => flight?.images || [])
)

export const selectVideoFlights = (state: RootState) => root(state).videoFlights
export const selectVideoFlightsMap = createSelector([selectVideoFlights], (flights) =>
    flights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, VideoFlight>
    )
)
export const selectVideoFlightById = memoize((id: string) =>
    createSelector([selectVideoFlightsMap], (map) => map[id] as VideoFlight)
)
export const selectVideoFlightIdsOrderedByDateCreated = createSelector(
    [selectVideoFlights],
    (flights) => {
        return flights
            .toSorted((a, b) => a.createdAt - b.createdAt)
            .map((flight) => ({ id: flight.id, createdAt: flight.createdAt }))
    }
)

export const selectLiveVideoFlightsFromProject = (state: RootState) => root(state).liveVideoFlights
// codes older than 24 hours are expired, if flight hasn't started yet, hide flight from rest of the application
export const selectLiveVideoFlights = createSelector(
    [selectLiveVideoFlightsFromProject],
    (flights) =>
        flights.filter(
            (e) =>
                !(
                    !e.startedAt &&
                    new Date().getTime() - dateFromNumber(e.createdAt).getTime() >
                        1000 * 60 * 60 * 24
                )
        )
)

export const selectAtlasLiveVideoFlights = createSelector([selectLiveVideoFlights], (flights) =>
    flights.filter((f) => f.isAtlas)
)
export const selectLiveAtlasLiveVideoFlights = createSelector(
    [selectAtlasLiveVideoFlights],
    (flights) => flights.filter((f) => f.live)
)

export const selectLiveVideoFlightIdsOrderedByDateCreated = createSelector(
    [selectLiveVideoFlights],
    (flights) => {
        return flights
            .toSorted((a, b) => a.createdAt - b.createdAt)
            .map((flight) => ({ id: flight.id, createdAt: flight.createdAt }))
    }
)

export const selectLiveLiveVideoFlights = createSelector([selectLiveVideoFlights], (flights) =>
    flights.filter((flight) => !!flight.live)
)
export const selectNonLiveLiveVideoFlights = createSelector([selectLiveVideoFlights], (flights) =>
    flights.filter((flight) => !flight.live)
)
export const selectStickyLiveVideoFlights = createSelector(
    [selectLiveLiveVideoFlights, selectStarredAtlasLiveVideoFlights],
    (flights, starredAtlasIds) => {
        return flights.filter((f) => !f.isAtlas || starredAtlasIds.includes(f.id))
    }
)

export const selectLiveVideoFlightsMap = createSelector([selectLiveVideoFlights], (flights) =>
    flights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, LiveVideoFlight>
    )
)
export const selectLiveVideoFlightById = memoize((id: string) =>
    createSelector([selectLiveVideoFlightsMap], (map) => map[id])
)

export const selectUserLocationShares = (state: RootState) => root(state).userLocationShares

export const selectLiveUserLocationShares = createSelector([selectUserLocationShares], (shares) =>
    shares.filter((share) => share.live)
)
export const selectNonLiveUserLocationShares = createSelector(
    [selectUserLocationShares],
    (shares) => shares.filter((share) => !share.live)
)

export const selectUserLocationSharesMap = createSelector([selectUserLocationShares], (shares) =>
    shares.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, UserLocationShare>
    )
)

export const selectUserLocationShareById = memoize((id: string) =>
    createSelector([selectUserLocationSharesMap], (map) => map[id])
)

export enum LiveVideoStatus {
    Standby = "standby",
    AwaitingCodeLink = "awaitingCodeLink",
    LivePending = "livePending",
    Live = "live",
    AwaitingRecording = "awaitingRecording",
    Completed = "completed",
    Expired = "expired",
}

const ONE_HOUR_MS = 60 * 60 * 1000

export function selectLiveVideoStatus(flight: LiveVideoFlight): LiveVideoStatus {
    if (flight.live && !flight.streamCreated) return LiveVideoStatus.LivePending
    if (flight.live) return LiveVideoStatus.Live
    if (flight.recordingReady || flight.recordingPrefix) return LiveVideoStatus.Completed
    if (flight.endedAt) {
        return flight.saveRecording ? LiveVideoStatus.AwaitingRecording : LiveVideoStatus.Completed
    }
    if (Date.now() - flight.createdAt * 1000 >= ONE_HOUR_MS) return LiveVideoStatus.Expired
    if (flight.code > 0 && !flight.startedAt) return LiveVideoStatus.AwaitingCodeLink
    return LiveVideoStatus.Standby
}

export const selectLiveVideoStatusById = memoize((id: string) =>
    createSelector([selectLiveVideoFlightById(id)], (flight) =>
        flight ? selectLiveVideoStatus(flight) : undefined
    )
)

export const selectVideoColorFinderFlights = (state: RootState) =>
    root(state).videoColorFinderFlights
export const selectVideoColorFinderFlightsMap = createSelector(
    [selectVideoColorFinderFlights],
    (flights) =>
        flights.reduce(
            (acc, cur) => {
                acc[cur.id] = cur
                return acc
            },
            {} as Record<string, VideoColorFinderFlight>
        )
)
export const selectVideoColorFinderFlightById = memoize((id: string) =>
    createSelector([selectVideoColorFinderFlightsMap], (map) => map[id] as VideoColorFinderFlight)
)

export const selectImageColorFinderFlights = (state: RootState) =>
    root(state).imageColorFinderFlights
export const selectImageColorFinderFlightsMap = createSelector(
    [selectImageColorFinderFlights],
    (flights) =>
        flights.reduce(
            (acc, cur) => {
                acc[cur.id] = cur
                return acc
            },
            {} as Record<string, ImageColorFinderFlight>
        )
)
export const selectImageColorFinderFlightById = memoize((id: string) =>
    createSelector([selectImageColorFinderFlightsMap], (map) => map[id])
)

export const selectColorFinderFlights = createSelector(
    [selectVideoColorFinderFlights, selectImageColorFinderFlights],
    (videoFlights, imageFlights) =>
        videoFlights.concat(imageFlights) as (VideoColorFinderFlight | ImageColorFinderFlight)[]
)

export const selectVisibleColorFinderFlights = createSelector(
    [selectColorFinderFlights, selectAllEntitiesMap],
    (flights, entityMap) =>
        flights.filter((flight) => {
            if (flight.archivedTimestamp) return false
            const stack = [] as (
                | VideoColorFinderFlight
                | ImageColorFinderFlight
                | SidePanelLayerOrGroup
            )[]
            stack.push(flight)
            while (stack.length > 0) {
                const curFlight = stack.pop()!
                if (!curFlight.visible) {
                    return false
                } else if (!curFlight.parentId || curFlight.parentId === "null") {
                    return true
                } else {
                    const parentFlight = entityMap[curFlight.parentId]
                    stack.push(parentFlight)
                }
            }
        })
)

export const selectColorFinderFlightById = memoize((flightId: string) =>
    createSelector(
        [selectVideoColorFinderFlightById(flightId), selectImageColorFinderFlightById(flightId)],
        (vcf, icf) => (vcf || icf) as ImageColorFinderFlight | VideoColorFinderFlight
    )
)

export const selectPanoramaFlights = (state: RootState) => root(state).panoramaFlights
export const selectPanoramaFlightsMap = createSelector([selectPanoramaFlights], (flights) =>
    flights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, PanoramaFlight>
    )
)
export const selectPanoramaFlightById = memoize((id: string) =>
    createSelector([selectPanoramaFlightsMap], (map) => map[id])
)

/** @deprecated */
export const selectFlightById = memoize((id: string) =>
    createSelector(
        [selectInfraredFlightById(id), selectOrthoFlightById(id)],
        (infraredFlight, orthoFlight) => {
            return infraredFlight || orthoFlight
        }
    )
)

const selectAllFlights = createSelector(
    [
        selectInfraredFlights,
        selectOrthoFlights,
        selectVideoFlights,
        selectColorFinderFlights,
        selectPanoramaFlights,
    ],
    (infraredFlights, orthoFlights, videoFlights, colorFinderFlights, panoramaFlights) => [
        ...infraredFlights,
        ...orthoFlights,
        ...videoFlights,
        ...colorFinderFlights,
        ...panoramaFlights,
    ]
)
export const selectAllNonArchivedFlights = createSelector(
    [
        selectNonArchivedInfraredFlights,
        selectNonArchivedOrthoFlights,
        selectVideoFlights,
        selectColorFinderFlights,
        selectPanoramaFlights,
    ],
    (infraredFlights, orthoFlights, videoFlights, colorFinderFlights, panoramaFlights) => [
        ...infraredFlights,
        ...orthoFlights,
        ...videoFlights,
        ...colorFinderFlights,
        ...panoramaFlights,
    ]
)

export const selectAllFlightsMap = createSelector([selectAllFlights], (allFlights) =>
    allFlights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<
            string,
            | InfraredFlight
            | OrthoFlight
            | VideoFlight
            | VideoColorFinderFlight
            | ImageColorFinderFlight
            | PanoramaFlight
        >
    )
)

let lastAllVisibleInfraredFlightsMap = {} as Record<string, InfraredFlight>
export const selectAllVisibleInfraredFlightsMap = createSelector(
    [selectInfraredFlights, selectAllEntitiesMap],
    (flights, entityMap) => {
        const nextAllVisibleInfraredFlightsMap = flights
            .filter((e) => {
                if (!e.mergeId) {
                    return (
                        e.visible &&
                        !getAncestors(e.id, entityMap).find((ancestor) => !ancestor.visible) &&
                        !e.archivedTimestamp
                    )
                }
                const mergedFlight = entityMap[e.mergeId]
                return (
                    !!mergedFlight &&
                    mergedFlight.visible &&
                    !getAncestors(mergedFlight.id, entityMap).find(
                        (ancestor) => !ancestor.visible
                    ) &&
                    !mergedFlight.archivedTimestamp
                )
            })
            .reduce(
                (acc, cur) => {
                    acc[cur.id] = cur
                    return acc
                },
                {} as Record<string, InfraredFlight>
            )
        if (
            Object.keys(lastAllVisibleInfraredFlightsMap).length ===
                Object.keys(nextAllVisibleInfraredFlightsMap).length &&
            Object.entries(lastAllVisibleInfraredFlightsMap).every(
                ([key, val]) => nextAllVisibleInfraredFlightsMap[key] === val
            )
        ) {
            return lastAllVisibleInfraredFlightsMap
        }
        lastAllVisibleInfraredFlightsMap = nextAllVisibleInfraredFlightsMap
        return lastAllVisibleInfraredFlightsMap
    }
)
export const selectVisibleOrthoFlightsMap = createSelector([selectVisibleOrthoFlights], (flights) =>
    flights.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, OrthoFlight>
    )
)
export const selectHotspotPointLayerIsVisible = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForHotspotPoints], (map) => !!map[flightId])
)

export const selectHotspotLabelLayerIsVisible = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForHotspotLabels], (map) => !!map[flightId])
)

export const selectRemovedHotspotsIsVisible = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForRemovedHotspots], (map) => !!map[flightId])
)

export const selectHotspotOrthoIsVisible = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForHotspotOrthos], (map) => !!map[flightId])
)

export const selectHotspotPolygonIsVisible = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForHotspotPolygons], (map) => !!map[flightId])
)

export const selectInfraredFlightPointColorById = memoize((flightId: string) =>
    createSelector([selectInfraredFlightById(flightId)], (flight) => flight?.pointColor)
)

export const selectInfraredFlightScanAreaFillColorById = memoize((flightId: string) =>
    createSelector([selectInfraredFlightById(flightId)], (flight) => flight?.scanAreaFillColor)
)

export const selectInfraredFlightScanAreaOutlineColorById = memoize((flightId: string) =>
    createSelector([selectInfraredFlightById(flightId)], (flight) => flight?.scanAreaOutlineColor)
)

export const selectOrthoFlightScanAreaFillColorById = memoize((flightId: string) =>
    createSelector([selectOrthoFlightById(flightId)], (flight) => flight?.scanAreaFillColor)
)

export const selectOrthoFlightScanAreaOutlineColorById = memoize((flightId: string) =>
    createSelector([selectOrthoFlightById(flightId)], (flight) => flight?.scanAreaOutlineColor)
)

export const selectInfraredFlightPolyStrokeById = memoize((flightId: string) =>
    createSelector([selectInfraredFlightById(flightId)], (flight) => flight?.polyStroke)
)

export const selectInfraredFlightPolyFillById = memoize((flightId: string) =>
    createSelector([selectInfraredFlightById(flightId)], (flight) => flight?.polyFill)
)

export const selectInfraredFlightOrthoGradientById = memoize((flightId: string) =>
    createSelector(
        [selectInfraredFlightById(flightId)],
        (flight) => flight?.orthoGradient as OrthoPalettes | undefined
    )
)

// export const selectHotspotPolygonAreaIsVisible =
//     (flightId = "") =>
//     (state: RootState): boolean =>
//         !!selectShowLayerByIdMapForHotspotPolygonAreas(state)[flightId]

export const selectFlightOrthoBoundingBox = memoize((flightId: string) =>
    createSelector(
        [selectOrthoFlightById(flightId), selectInfraredFlightById(flightId)],
        (maybeOrthoFlight, maybeInfraredFlight) =>
            maybeOrthoFlight?.orthoBoundingBox || maybeInfraredFlight?.orthoBoundingBox
    )
)

export const selectIsAnyHotspotLayerShowing = memoize((flightId: string) =>
    createSelector(
        [
            selectHotspotPointLayerIsVisible(flightId),
            selectHotspotOrthoIsVisible(flightId),
            selectHotspotPolygonIsVisible(flightId),
            selectShowLayerByIdMapForFlightPaths,
        ],
        (isPointsVisible, isOrthoVisible, isPolygonVisible, flightPathShowMap) => {
            return (
                isPointsVisible || isOrthoVisible || isPolygonVisible || flightPathShowMap[flightId]
            )
        }
    )
)

export const createLayerMapSelector = <T extends InfraredFlight | OrthoFlight>(
    layerMapSelector: (state: RootState) => ShowLayerByIdMap<ID>,
    selectVisibleFlights: (state: RootState) => Record<string, T>
) =>
    createSelector([layerMapSelector, selectVisibleFlights], (layerMap, visibleFlights) => {
        return Object.keys(layerMap).reduce((result, layerId) => {
            if (visibleFlights[layerId]) {
                result[layerId] = layerMap[layerId]
            }
            return result
        }, {} as ShowLayerByIdMap<ID>)
    })

export const selectShowLayerByIdMapForHotspotPoints = (state: RootState) =>
    state.map.layers.show.hotspotPoints

export const selectShowLayerByIdMapForHotspotLabels = (state: RootState) =>
    state.map.layers.show.hotspotLabels

export const selectShowLayerByIdMapForRemovedHotspots = (state: RootState) =>
    state.map.layers.show.removedHotspots

export const selectShowLayerByIdMapForHotspotOrthos = (state: RootState) =>
    state.map.layers.show.hotspotOrthos

export const selectShowLayerByIdMapForHotspotPolygons = (state: RootState) =>
    state.map.layers.show.hotspotPolygons

export const selectShowLayerByIdMapForFlightPaths = (
    state: RootState
): ShowLayerByIdMap<FlightId> => {
    return state.map.layers.show.flightPath
}

export const selectFlightPathIsEnabled = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForFlightPaths], (map) => !!map[flightId])
)

export const selectShowLayerByIdMapForHotspotScanArea = (state: RootState) =>
    state.map.layers.show.hotspotScanArea

export const selectHotspotScanAreaIsEnabled = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForHotspotScanArea], (map) => !!map[flightId])
)

export const selectShowLayerByIdMapForOrthoScanArea = (state: RootState) =>
    state.map.layers.show.orthoScanArea

export const selectOrthoScanAreaIsEnabled = memoize((flightId: string) =>
    createSelector([selectShowLayerByIdMapForOrthoScanArea], (map) => !!map[flightId])
)
export const selectHoveredBoundingBoxId = (state: RootState) =>
    state.map.layers.hoveredBoundingBoxId

export const selectFlightsWithHotspotLabels = createLayerMapSelector(
    selectShowLayerByIdMapForHotspotLabels,
    selectAllVisibleInfraredFlightsMap
)

export const selectFlightsWithRemovedHotspots = createLayerMapSelector(
    selectShowLayerByIdMapForRemovedHotspots,
    selectAllVisibleInfraredFlightsMap
)

export const selectFlightsWithHotspotPolygons = createLayerMapSelector(
    selectShowLayerByIdMapForHotspotPolygons,
    selectAllVisibleInfraredFlightsMap
)

export const selectFlightsWithHotspotPoints = createLayerMapSelector(
    selectShowLayerByIdMapForHotspotPoints,
    selectAllVisibleInfraredFlightsMap
)

export const selectFlightsWithHotspotOrthos = createLayerMapSelector(
    selectShowLayerByIdMapForHotspotOrthos,
    selectAllVisibleInfraredFlightsMap
)

export const selectFlightsWithHotspotScanArea = createLayerMapSelector(
    selectShowLayerByIdMapForHotspotScanArea,
    selectAllVisibleInfraredFlightsMap
)

export const selectFlightsWithOrthoScanArea = createLayerMapSelector(
    selectShowLayerByIdMapForOrthoScanArea,
    selectVisibleOrthoFlightsMap
)

export const selectHotspotOrthoVisibilityMap = createLayerMapSelector(
    selectShowLayerByIdMapForHotspotOrthos,
    selectAllVisibleInfraredFlightsMap
)

export const selectFlightIsBoundingBoxShowing = memoize((flightId: string) =>
    createSelector([(state) => state.map.layers.show], (show) => {
        if (!flightId) return false
        //only these four layers make up the bounding box:
        const layers = ["hotspotPoints", "hotspotOrthos", "hotspotPolygons", "flightPath"]
        if (!show) return false

        let result = false

        Object.entries(show as Record<string, Record<string, boolean>>).forEach(
            ([layerName, ids]) => {
                if (!layers.includes(layerName)) {
                    return
                }
                const isShowing = ids[flightId]
                if (isShowing) result = isShowing
            }
        )
        return result
    })
)

export const selectHasVisibleLabels = createSelector(
    [selectFlightsWithHotspotLabels],
    (flights) => !!Object.keys(flights).length
)

export const selectHoveredOrthoFlightId = createSelector(
    [selectNonArchivedOrthoFlights, selectHoveredBoundingBoxId],
    (flights, id) => (flights.some((flight) => flight.id === id) ? id : undefined)
)

export const selectShowLayerByIdMapForVisibleHotspotPoints = createSelector(
    [selectShowLayerByIdMapForHotspotPoints, selectAllVisibleInfraredFlightsMap],
    (showHotspotPointsMap, visibleFlights) => {
        return Object.entries(showHotspotPointsMap).reduce((prev, curr) => {
            const [id, value] = curr
            if (!!visibleFlights[id]) return { ...prev, [id]: value }
            return prev
        }, {} as ShowLayerByIdMap<string>)
    }
)

export const selectShowLayerByIdMapForVisibleHotspotPolygons = createSelector(
    [selectShowLayerByIdMapForHotspotPolygons, selectAllVisibleInfraredFlightsMap],
    (showHotspotPolygonsMap, visibleFlights) => {
        return Object.entries(showHotspotPolygonsMap).reduce((prev, curr) => {
            const [id, value] = curr
            if (!!visibleFlights[id]) return { ...prev, [id]: value }
            return prev
        }, {} as ShowLayerByIdMap<string>)
    }
)
